import { useState, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import { Upload as UploadIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material'
import { studentsApi, uploadApi, analysisApi } from '../lib/api'
import { Student, AnalysisTask } from '../types'

export default function Analysis() {
  const [students, setStudents] = useState<Student[]>([])
  const [selectedStudent, setSelectedStudent] = useState<string>('')
  const [files, setFiles] = useState<File[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [tasks, setTasks] = useState<AnalysisTask[]>([])
  const [error, setError] = useState('')

  // 加载学生列表
  useEffect(() => {
    studentsApi.getAll().then(setStudents).catch(console.error)
  }, [])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles].slice(0, 10))
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpg', '.jpeg', '.png', '.webp'] },
    maxFiles: 10,
  })

  const handleUpload = async () => {
    if (!selectedStudent || files.length === 0) {
      setError('请选择学生并添加图片')
      return
    }

    setUploading(true)
    setUploadProgress(0)
    setError('')

    try {
      const imageIds: string[] = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        const result = await uploadApi.uploadImage(file, selectedStudent)
        imageIds.push(result.id)
        setUploadProgress(((i + 1) / files.length) * 80)
      }

      const task = await analysisApi.create({
        studentId: selectedStudent,
        imageIds,
      })

      setTasks(prev => [task, ...prev])
      setFiles([])
      setUploadProgress(100)

      pollTaskStatus(task.id)
    } catch (err: any) {
      setError(err.message || '上传或分析失败')
    } finally {
      setUploading(false)
    }
  }

  const pollTaskStatus = async (taskId: string) => {
    const interval = setInterval(async () => {
      try {
        const task = await analysisApi.getTask(taskId)
        setTasks(prev => prev.map(t => (t.id === taskId ? task : t)))

        if (task.status === 'completed' || task.status === 'failed') {
          clearInterval(interval)
        }
      } catch (err) {
        clearInterval(interval)
      }
    }, 2000)

    setTimeout(() => clearInterval(interval), 5 * 60 * 1000)
  }

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        作业分析
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. 选择学生
              </Typography>
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>选择学生</InputLabel>
                  <Select
                    value={selectedStudent}
                    label="选择学生"
                    onChange={e => setSelectedStudent(e.target.value)}
                  >
                    <MenuItem value="">请选择学生...</MenuItem>
                    {students.map(s => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.name} - {s.grade}{s.class}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Typography variant="h6" gutterBottom>
                2. 上传作业图片
              </Typography>
              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: isDragActive ? 'primary.light' : 'background.default',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.light' },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" gutterBottom>
                  {isDragActive ? '释放以上传' : '拖拽图片到此处，或点击选择'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  支持 JPG、PNG、WebP，最多10张
                </Typography>
              </Box>

              {files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    已选择 {files.length} 张图片：
                  </Typography>
                  <List dense>
                    {files.map((file, idx) => (
                      <ListItem
                        key={idx}
                        secondaryAction={
                          <Button size="small" color="error" onClick={() => removeFile(idx)}>
                            移除
                          </Button>
                        }
                      >
                        <ListItemText
                          primary={file.name}
                          secondary={`${(file.size / 1024).toFixed(1)} KB`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {uploading && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" gutterBottom>
                    上传中... {Math.round(uploadProgress)}%
                  </Typography>
                  <LinearProgress variant="determinate" value={uploadProgress} />
                </Box>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                startIcon={<UploadIcon />}
                onClick={handleUpload}
                disabled={uploading || !selectedStudent || files.length === 0}
                sx={{ mt: 3 }}
              >
                {uploading ? '处理中...' : '开始分析'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                分析任务
              </Typography>
              {tasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  暂无任务
                </Typography>
              ) : (
                <List>
                  {tasks.map(task => (
                    <ListItem
                      key={task.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        p: 2,
                      }}
                    >
                      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {task?.id?.slice(0, 8) || '------'}...
                        </Typography>
                        <Chip
                          label={task?.status || 'unknown'}
                          size="small"
                          color={
                            task.status === 'completed'
                              ? 'success'
                              : task.status === 'failed'
                              ? 'error'
                              : task.status === 'processing'
                              ? 'info'
                              : 'default'
                          }
                        />
                      </Box>
                      {task.status === 'processing' && (
                        <Box sx={{ width: '100%' }}>
                          <LinearProgress variant="determinate" value={task.progress} />
                          <Typography variant="caption" color="text.secondary">
                            {task.progress}%
                          </Typography>
                        </Box>
                      )}
                      {task.status === 'completed' && task.result && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          已生成 {task.result.exercises.length} 道练习题
                        </Typography>
                      )}
                      {task.status === 'failed' && (
                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                          {task.error || '分析失败'}
                        </Typography>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
